import { useState } from "react";
import type { CampaignSummary, CampaignMember, CampaignQuadraProgress } from "@/types/campaign";
import {
  CAMPAIGN_TYPE_LABELS,
  CAMPAIGN_STATUS_LABELS,
  CAMPAIGN_STATUS_COLORS,
  MEMBER_ROLE_LABELS,
} from "@/types/campaign";
import { useCampaignMembers, useCampaignQuadras } from "@/hooks";

interface Props {
  campaign: CampaignSummary;
  onClose: () => void;
  onRegisterTree: () => void;
}

type TabKey = "resumo" | "membros" | "quadras";

export default function CampaignDetailDrawer({ campaign, onClose, onRegisterTree }: Props) {
  const [activeTab, setActiveTab] = useState<TabKey>("resumo");
  const { members, loading: membersLoading } = useCampaignMembers(campaign.id);
  const { quadras, loading: quadrasLoading } = useCampaignQuadras(campaign.id);

  const statusLabel = CAMPAIGN_STATUS_LABELS[campaign.status] ?? campaign.status;
  const statusColor = CAMPAIGN_STATUS_COLORS[campaign.status] ?? "bg-gray-100 text-gray-600";
  const typeLabel = CAMPAIGN_TYPE_LABELS[campaign.campaignType] ?? campaign.campaignType;
  const progressPct = Math.min(100, Math.round(campaign.progressPercent));

  const tabs: { key: TabKey; label: string }[] = [
    { key: "resumo", label: "Resumo" },
    { key: "membros", label: `Membros (${members.length})` },
    { key: "quadras", label: `Quadras (${quadras.length})` },
  ];

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <div className="relative w-full max-w-lg bg-white shadow-xl overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 p-5 z-10">
          <div className="flex items-start justify-between">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-xs font-mono text-foreground-500">{campaign.code}</span>
                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${statusColor}`}>
                  {statusLabel}
                </span>
              </div>
              <h2 className="text-lg font-semibold text-foreground-900">{campaign.name}</h2>
              <p className="text-sm text-foreground-500 mt-0.5">{typeLabel}</p>
            </div>
            <button
              onClick={onClose}
              className="p-1.5 text-foreground-400 hover:text-foreground-700 rounded"
            >
              <i className="ri-close-line text-xl" />
            </button>
          </div>

          {/* Tabs */}
          <div className="flex gap-4 mt-4">
            {tabs.map((t) => (
              <button
                key={t.key}
                onClick={() => setActiveTab(t.key)}
                className={`text-sm pb-2 border-b-2 transition ${
                  activeTab === t.key
                    ? "border-emerald-600 text-emerald-700 font-medium"
                    : "border-transparent text-foreground-500 hover:text-foreground-700"
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="p-5">
          {activeTab === "resumo" && (
            <ResumoTab
              campaign={campaign}
              progressPct={progressPct}
              onRegisterTree={onRegisterTree}
            />
          )}
          {activeTab === "membros" && (
            <MembrosTab members={members} loading={membersLoading} />
          )}
          {activeTab === "quadras" && (
            <QuadrasTab quadras={quadras} loading={quadrasLoading} />
          )}
        </div>
      </div>
    </div>
  );
}

function ResumoTab({
  campaign,
  progressPct,
  onRegisterTree,
}: {
  campaign: CampaignSummary;
  progressPct: number;
  onRegisterTree: () => void;
}) {
  return (
    <div className="space-y-5">
      {/* Progress */}
      <div>
        <div className="flex items-center justify-between text-sm mb-1">
          <span className="text-foreground-600">Progresso geral</span>
          <span className="font-medium">{progressPct}%</span>
        </div>
        <div className="w-full h-3 bg-gray-100 rounded-full overflow-hidden">
          <div
            className="h-full bg-emerald-500 rounded-full transition-all"
            style={{ width: `${progressPct}%` }}
          />
        </div>
      </div>

      {/* Stats grid */}
      <div className="grid grid-cols-2 gap-3">
        <StatCard label="Membros" value={campaign.memberCount} icon="ri-team-line" />
        <StatCard label="Quadras" value={campaign.quadraCount} icon="ri-map-2-line" />
        <StatCard label="Árvores registradas" value={campaign.registeredTreeCount} icon="ri-plant-line" />
        <StatCard
          label="Meta de árvores"
          value={campaign.targetTreeCount ?? "—"}
          icon="ri-flag-line"
        />
        <StatCard label="Quadras concluídas" value={campaign.quadrasConcluidas} icon="ri-check-double-line" />
      </div>

      {/* Description */}
      {campaign.description && (
        <div>
          <h4 className="text-xs font-semibold text-foreground-500 uppercase mb-1">Descrição</h4>
          <p className="text-sm text-foreground-700">{campaign.description}</p>
        </div>
      )}

      {/* Project / Area */}
      <div>
        <h4 className="text-xs font-semibold text-foreground-500 uppercase mb-1">Vinculação</h4>
        <div className="text-sm text-foreground-700 space-y-1">
          <div>
            <span className="text-foreground-400">Projeto:</span> {campaign.projectName}
          </div>
          {campaign.areaPilotoName && (
            <div>
              <span className="text-foreground-400">Área piloto:</span> {campaign.areaPilotoName}
            </div>
          )}
        </div>
      </div>

      {/* Dates */}
      <div>
        <h4 className="text-xs font-semibold text-foreground-500 uppercase mb-1">Datas</h4>
        <div className="text-sm text-foreground-700 space-y-1">
          {campaign.startDate && (
            <div>
              <span className="text-foreground-400">Início:</span>{" "}
              {new Date(campaign.startDate).toLocaleDateString("pt-BR")}
            </div>
          )}
          {campaign.endDate && (
            <div>
              <span className="text-foreground-400">Término:</span>{" "}
              {new Date(campaign.endDate).toLocaleDateString("pt-BR")}
            </div>
          )}
          <div>
            <span className="text-foreground-400">Criada em:</span>{" "}
            {new Date(campaign.createdAt).toLocaleDateString("pt-BR")}
          </div>
        </div>
      </div>

      {/* Notes */}
      {campaign.notes && (
        <div>
          <h4 className="text-xs font-semibold text-foreground-500 uppercase mb-1">Observações</h4>
          <p className="text-sm text-foreground-700">{campaign.notes}</p>
        </div>
      )}

      {/* Quick register button */}
      {campaign.status === "EM_ANDAMENTO" && (
        <button
          onClick={onRegisterTree}
          className="w-full py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-sm font-medium transition flex items-center justify-center gap-2"
        >
          <i className="ri-add-circle-line" />
          Registrar árvore (FIQ-1)
        </button>
      )}
    </div>
  );
}

function StatCard({ label, value, icon }: { label: string; value: string | number; icon: string }) {
  return (
    <div className="bg-gray-50 rounded-lg p-3">
      <div className="flex items-center gap-2 mb-1">
        <i className={`${icon} text-emerald-600`} />
        <span className="text-xs text-foreground-400">{label}</span>
      </div>
      <div className="text-lg font-semibold text-foreground-800">{value}</div>
    </div>
  );
}

function MembrosTab({ members, loading }: { members: CampaignMember[]; loading: boolean }) {
  if (loading) return <p className="text-sm text-foreground-400">Carregando membros...</p>;
  if (members.length === 0) return <p className="text-sm text-foreground-400">Nenhum membro cadastrado.</p>;

  return (
    <div className="space-y-3">
      {members.map((m) => (
        <div key={m.id} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
          <div className="w-9 h-9 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center font-semibold text-sm">
            {(m.memberName ?? "?")[0].toUpperCase()}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium text-foreground-800 truncate">
              {m.memberName ?? "Sem nome"}
            </div>
            <div className="text-xs text-foreground-400">
              {MEMBER_ROLE_LABELS[m.memberRole] ?? m.memberRole}
              {m.professionalCouncil && ` / ${m.professionalCouncil} ${m.professionalRegistration ?? ""}`}
            </div>
          </div>
          {!m.isActive && (
            <span className="text-xs px-2 py-0.5 rounded-full bg-gray-200 text-gray-500">Inativo</span>
          )}
        </div>
      ))}
    </div>
  );
}

function QuadrasTab({ quadras, loading }: { quadras: CampaignQuadraProgress[]; loading: boolean }) {
  if (loading) return <p className="text-sm text-foreground-400">Carregando quadras...</p>;
  if (quadras.length === 0) return <p className="text-sm text-foreground-400">Nenhuma quadra vinculada.</p>;

  const statusColors: Record<string, string> = {
    PENDENTE: "bg-gray-100 text-gray-600",
    EM_ANDAMENTO: "bg-blue-100 text-blue-700",
    CONCLUIDA: "bg-green-100 text-green-700",
    PAUSADA: "bg-yellow-100 text-yellow-700",
  };

  return (
    <div className="space-y-3">
      {quadras.map((q) => {
        const pct = Math.min(100, Math.round(q.progressPercent));
        const color = statusColors[q.status] ?? "bg-gray-100 text-gray-600";
        return (
          <div key={q.id} className="p-3 bg-gray-50 rounded-lg">
            <div className="flex items-center justify-between mb-2">
              <div>
                <span className="text-sm font-medium text-foreground-800">{q.quadraCode}</span>
                {q.quadraName && (
                  <span className="text-xs text-foreground-400 ml-2">{q.quadraName}</span>
                )}
              </div>
              <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${color}`}>
                {q.status === "CONCLUIDA" ? "Concluída" : q.status === "EM_ANDAMENTO" ? "Em andamento" : q.status === "PAUSADA" ? "Pausada" : "Pendente"}
              </span>
            </div>
            <div className="flex items-center justify-between text-xs text-foreground-500 mb-1">
              <span>{q.registeredTreeCount}{q.targetTreeCount ? ` / ${q.targetTreeCount}` : ""} árvores</span>
              <span>{pct}%</span>
            </div>
            <div className="w-full h-1.5 bg-gray-200 rounded-full overflow-hidden">
              <div className="h-full bg-emerald-500 rounded-full" style={{ width: `${pct}%` }} />
            </div>
          </div>
        );
      })}
    </div>
  );
}
